// src/mobile/pages/Administration/StartPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { setAccessToken } from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import HighLogo from "../../components/HighLogo";
import logo from "../../../assets/logo.png";
import Modal from '../../components/Modal';
import StartFooter from "./StartFooter";

const StartPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { setToken, setUser, setE2EEPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // ---- 2FA UI state ----
  const [twoFAOpen, setTwoFAOpen] = useState(false);
  const [temp2FAToken, setTemp2FAToken] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefetching to Gallery
  const prefetchBlog = async () => {
    try {
      const res = await api.get('/media', {
        params: { city: 'Berlin', country: 'Germany', limit: 4 },
      });
      localStorage.setItem('prefetchedMediaPosts', JSON.stringify({
        value: res.data,
        expiry: Date.now() + 30 * 1000
      }));
      console.log("✅ Media prefetch successful (15 items, 30s expiry)");
    } catch (err) {
      console.warn("⚠️ Media prefetch failed:", err);
    }
  };

  // Hard Load Gallery
  const GALLERY_HARD_RELOAD_KEY = 'gallery:hardReload';

  const goToGalleryHard = (replace = true) => {
    sessionStorage.setItem(GALLERY_HARD_RELOAD_KEY, '1');
    navigate(`/gallery?refresh=${Date.now()}`, { replace });
  };


  async function finalizeLoginAndGoHome(accessToken: string) {
    // Store access token in memory (NOT localStorage)
    setAccessToken(accessToken);
    setToken(accessToken);
  
    // OPTIONAL:
    // If you want AuthContext to be the single source of truth,
    // you can remove this profile fetch + localStorage caching.
    const profileRes = await api.get("/users/me"); // interceptor attaches Authorization
    const profile = profileRes.data;
  
    setUser(profile);
  
    // If you still need this call for your E2EE flow, keep it.
    // In your newer AuthContext version it's a no-op, but harmless.
    setE2EEPassword(password);
  
    await prefetchBlog();
    goToGalleryHard();
  }
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
  
    try {
      const response = await api.post("/auth/login", { email, password });
  
      // 2FA required
      if (response?.data?.twoFARequired && response?.data?.token) {
        const temp = response.data.token as string;
        setTemp2FAToken(temp);
        setTwoFAOpen(true);
  
        try {
          await api.post("/security/2fa/email/request-login", { token: temp });
        } catch (reqErr: any) {
          setError(
            reqErr?.response?.data?.msg ||
            reqErr?.message ||
            t("twoFA.requestFailed")
          );
        }
        return;
      }
  
      // Normal login
      const accessToken = response?.data?.accessToken || response?.data?.token;
      if (!accessToken) throw new Error("No access token returned from server.");
  
      // ✅ Do it once
      await finalizeLoginAndGoHome(accessToken);
    } catch (err: any) {
      const message =
        err.response?.data?.msg ||
        err.message ||
        t("startPage.invalidCredentials");
      setError(message);
      alert(message);
    }
  };

  // When user submits the 6-digit code
  const handleVerify2FA = async () => {
    setBusy(true);
    setError(null);
  
    try {
      if (!temp2FAToken) throw new Error(t("twoFA.missingToken"));
      if (!/^\d{6}$/.test(otp.trim())) {
        setError(t("twoFA.invalidCode"));
        return;
      }
  
      const verifyRes = await api.post("/security/2fa/email/verify-login", {
        token: temp2FAToken,
        otp: otp.trim(),
      });
  
      const realAccessToken = verifyRes?.data?.accessToken || verifyRes?.data?.token;
      if (!realAccessToken) throw new Error("No auth token returned.");
  
      // ✅ Call once
      await finalizeLoginAndGoHome(realAccessToken);
  
      setTwoFAOpen(false);
      setOtp("");
      setTemp2FAToken(null);
    } catch (err: any) {
      console.error("2FA verify error:", err);
      setError(
        err?.response?.data?.msg ||
        err?.message ||
        t("twoFA.verifyFailed")
      );
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = () => navigate('/forgot-password');
  const handleCreateAccount = () => navigate('/profile-setup');  

return (
  <div style={pageShell}>
    <header style={logoHeaderStyle}>
      <button
        type="button"
        onClick={() => navigate("/")}
        aria-label="Go to homepage"
        style={logoButtonStyle}
      >
        <HighLogo
          src={logo}
          style={{
            width: 170,
            maxWidth: "62vw",
            display: "block",
          }}
          padding={0}
        />
      </button>
    </header>

    <main style={mainStyle}>
      <section style={loginPanel}>
        <h1 style={titleStyle}>
          <span style={{ color: "#f8e298" }}>
            {t("startPage.loginTitlelogin")}
          </span>

          <span style={{ color: "#4b8fe2" }}>
            {" "}
            {t("startPage.loginTitlehappening")}
          </span>
        </h1>

        {!!error && <div style={errorBox}>{error}</div>}

        <form onSubmit={handleLogin} style={formStyle}>
          <div style={floatingField}>
            <label
              htmlFor="mobile-login-email"
              style={{
                ...floatingLabel,
                top: emailFocused || email ? 8 : 18,
                fontSize: emailFocused || email ? 11 : 16,
                color: emailFocused ? "#60a5fa" : "#94a3b8",
              }}
            >
              {t("email")}
            </label>

            <input
              id="mobile-login-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              style={{
                ...floatingInput,
                borderColor: emailFocused
                  ? "rgba(96,165,250,0.75)"
                  : "rgba(148,163,184,0.25)",
                boxShadow: emailFocused
                  ? "0 0 0 3px rgba(59,130,246,0.1)"
                  : "inset 0 1px 0 rgba(255,255,255,0.03)",
              }}
              autoComplete="email"
              required
            />
          </div>

          <div style={floatingField}>
            <label
              htmlFor="mobile-login-password"
              style={{
                ...floatingLabel,
                top: passwordFocused || password ? 8 : 18,
                fontSize: passwordFocused || password ? 11 : 16,
                color: passwordFocused ? "#60a5fa" : "#94a3b8",
              }}
            >
              {t("password")}
            </label>

            <input
              id="mobile-login-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              style={{
                ...floatingInput,
                borderColor: passwordFocused
                  ? "rgba(96,165,250,0.75)"
                  : "rgba(148,163,184,0.25)",
                boxShadow: passwordFocused
                  ? "0 0 0 3px rgba(59,130,246,0.1)"
                  : "inset 0 1px 0 rgba(255,255,255,0.03)",
              }}
              autoComplete="current-password"
              required
            />
          </div>

          <button type="submit" style={submitBtn}>
            {t("login")}
          </button>

          <button
            type="button"
            onClick={handleForgotPassword}
            style={forgotLink}
          >
            {t("forgotPassword")}
          </button>

          <button
            type="button"
            onClick={handleCreateAccount}
            style={createBtn}
          >
            {t("createAccount")}
          </button>
        </form>
      </section>
    </main>

    <StartFooter />

    <Modal
      open={twoFAOpen}
      onClose={() => {
        if (!busy) {
          setTwoFAOpen(false);
          setOtp("");
        }
      }}
      title={t("twoFA.title")}
      closeOnOverlayClick={false}
    >
      <>
        <p style={{ marginTop: 0, fontSize: 14 }}>
          {t("twoFA.intro")}
        </p>

        <input
          inputMode="numeric"
          pattern="\d*"
          value={otp}
          onChange={(event) =>
            setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))
          }
          placeholder={t("twoFA.placeholder")}
          maxLength={6}
          style={modalInputStyle}
          autoFocus
        />

        <div style={modalActionsStyle}>
          <button
            type="button"
            onClick={() => {
              setTwoFAOpen(false);
              setOtp("");
              setTemp2FAToken(null);
              setError(null);
            }}
            disabled={busy}
            style={{
              ...modalCancelButtonStyle,
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.65 : 1,
            }}
          >
            {t("twoFA.cancel")}
          </button>

          <button
            type="button"
            onClick={handleVerify2FA}
            disabled={busy || otp.trim().length !== 6}
            style={{
              ...modalVerifyButtonStyle,
              cursor:
                busy || otp.trim().length !== 6
                  ? "default"
                  : "pointer",
              opacity:
                busy || otp.trim().length !== 6
                  ? 0.65
                  : 1,
            }}
          >
            {busy ? t("twoFA.verifying") : t("twoFA.verify")}
          </button>
        </div>

        <div style={modalHintStyle}>
          {t("twoFA.hint")}
        </div>
      </>
    </Modal>
  </div>
);
  
  
};

const pageShell: React.CSSProperties = {
  minHeight: "100dvh",
  width: "100%",
  overflowX: "hidden",
  background:
    "radial-gradient(circle at top, #020617 0, #000 44%, #020617 100%)",
  color: "#f9fafb",
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: "flex",
  flexDirection: "column",
};

const logoHeaderStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding:
    "calc(24px + env(safe-area-inset-top)) 20px 8px",
  boxSizing: "border-box",
};

const logoButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  touchAction: "manipulation",
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "22px 20px 52px",
  boxSizing: "border-box",
};

const loginPanel: React.CSSProperties = {
  width: "100%",
  maxWidth: 430,
  boxSizing: "border-box",
};

const titleStyle: React.CSSProperties = {
  width: "100%",
  margin: "0 0 42px",
  fontSize: "clamp(30px, 8.5vw, 38px)",
  lineHeight: 1.08,
  letterSpacing: "-1.25px",
  fontWeight: 800,
  textAlign: "center",
};

const formStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 18,
};

const floatingField: React.CSSProperties = {
  position: "relative",
  width: "100%",
};

const floatingLabel: React.CSSProperties = {
  position: "absolute",
  left: 18,
  zIndex: 1,
  transition:
    "top 0.18s ease, font-size 0.18s ease, color 0.18s ease",
  pointerEvents: "none",
  fontWeight: 500,
};

const floatingInput: React.CSSProperties = {
  width: "100%",
  height: 58,
  padding: "23px 18px 4px",
  borderRadius: 16,
  border: "1px solid rgba(148,163,184,0.25)",
  background: "rgba(15,23,42,0.45)",
  color: "#f8fafc",
  fontSize: 16,
  outline: "none",
  boxSizing: "border-box",
  transition:
    "border-color 0.18s ease, box-shadow 0.18s ease",
};

const submitBtn: React.CSSProperties = {
  width: "100%",
  minHeight: 50,
  padding: "13px 18px",
  marginTop: 10,
  borderRadius: 999,
  border: "none",
  background:
    "linear-gradient(135deg, rgba(212,175,55,1), rgba(250,250,210,0.95))",
  color: "#111827",
  cursor: "pointer",
  fontSize: 15,
  fontWeight: 800,
  boxShadow: "0 12px 30px rgba(212,175,55,0.18)",
  touchAction: "manipulation",
};

const forgotLink: React.CSSProperties = {
  alignSelf: "center",
  minHeight: 36,
  padding: "6px 10px",
  border: "none",
  background: "transparent",
  color: "#93c5fd",
  cursor: "pointer",
  fontSize: 14,
  touchAction: "manipulation",
};

const createBtn: React.CSSProperties = {
  width: "100%",
  minHeight: 50,
  padding: "13px 18px",
  marginTop: 16,
  borderRadius: 999,
  border: "1px solid rgba(163,230,53,0.32)",
  background: "rgba(34,197,94,0.08)",
  color: "#bef264",
  cursor: "pointer",
  fontSize: 15,
  fontWeight: 700,
  touchAction: "manipulation",
};

const errorBox: React.CSSProperties = {
  marginBottom: 18,
  padding: "11px 13px",
  borderRadius: 12,
  color: "#fed7aa",
  background: "rgba(127,29,29,0.3)",
  border: "1px solid rgba(248,113,113,0.6)",
  fontSize: 13,
  lineHeight: 1.45,
};

const modalInputStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px",
  marginTop: 6,
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  fontSize: 18,
  textAlign: "center",
  letterSpacing: 5,
  boxSizing: "border-box",
};

const modalActionsStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  marginTop: 14,
  justifyContent: "flex-end",
};

const modalCancelButtonStyle: React.CSSProperties = {
  minHeight: 42,
  padding: "10px 14px",
  borderRadius: 10,
  border: "none",
  background: "#4b5563",
  color: "#f3f4f6",
  fontWeight: 600,
};

const modalVerifyButtonStyle: React.CSSProperties = {
  minHeight: 42,
  padding: "10px 14px",
  borderRadius: 10,
  border: "none",
  background: "#16a34a",
  color: "#f9fafb",
  fontWeight: 700,
};

const modalHintStyle: React.CSSProperties = {
  marginTop: 10,
  fontSize: 12,
  color: "#6b7280",
  lineHeight: 1.45,
};
export default StartPage;
